import {IconButton} from '@dropbox/dig-components/buttons';
import {Text} from '@dropbox/dig-components/typography';
import {Box, Split, Stack} from '@dropbox/dig-foundations';
import {UIIcon} from '@dropbox/dig-icons';
import {ChevronLeftLine, ChevronRightLine} from '@dropbox/dig-icons/assets';
import {Link} from 'components/DSYS/Link';
import {t} from 'i18next';
import {ReactNode, useEffect, useRef, useState} from 'react';
import {logDashboardInteraction} from 'views/home/dashboard/dashboardAnalytics';
import {Tile} from 'views/home/dashboard/Tile';

import {getSelectableQuarters, quarterId} from './earningsData';
import {EarningsInfoTooltip} from './EarningsInfoTooltip';
import {EarningsQuarterMenu} from './EarningsQuarterMenu';
import {MetricSlide, OutlookSlide} from './EarningsSlides';
import styles from './EarningsTile.module.css';

const ExternalLink = ({
  to,
  target,
  quarter,
  children,
}: {
  to: string;
  target: string;
  /** Quarter id the link belongs to, so clicks can be split by quarter. */
  quarter: string;
  children: ReactNode;
}) => (
  <Text size="xsmall">
    <Link
      to={to}
      openInNewTab
      hasNoUnderline
      showUnderlineOnHover
      onClick={() =>
        logDashboardInteraction({action: 'link_click', tileId: 'earnings', target, value: quarter})
      }
    >
      {children}
    </Link>
  </Text>
);

/** Page indicator; each dot jumps to its slide. */
const PageDots = ({
  count,
  active,
  labels,
  onSelect,
}: {
  count: number;
  active: number;
  labels: string[];
  onSelect: (i: number) => void;
}) => (
  <Box display="flex" alignItems="center" style={{gap: 4}}>
    {Array.from({length: count}, (_, i) => (
      <button
        key={labels[i]}
        type="button"
        aria-label={labels[i]}
        aria-current={i === active ? 'true' : undefined}
        className={`${styles.dot} ${i === active ? styles.dotActive : ''}`}
        onClick={() => onSelect(i)}
      />
    ))}
  </Box>
);

/**
 * Quarterly results: one metric per page, swipe or arrow through, outlook last.
 * Opens on the newest published quarter; the header menu switches to earlier
 * quarters of the same fiscal year.
 */
export const EarningsTile = () => {
  const [quarters] = useState(() => getSelectableQuarters());
  const [selectedId, setSelectedId] = useState(() => (quarters[0] ? quarterId(quarters[0]) : ''));
  const d = quarters.find((q) => quarterId(q) === selectedId) ?? quarters[0];
  const pageLabels = [
    ...(d?.metrics ?? []).map((m) => m.label),
    t('dashboard_earnings_outlook').toString(),
  ];
  const pageCount = pageLabels.length;
  const [active, setActive] = useState(0);
  const scroller = useRef<HTMLDivElement>(null);
  const slides = useRef<(HTMLDivElement | null)[]>([]);
  // Page an arrow/dot click is scrolling to. Clicks build on it (like the
  // calendar's month arrows) so a quick double-click moves two pages, not one.
  const pending = useRef<number | null>(null);

  const goTo = (i: number) => {
    const idx = Math.max(0, Math.min(pageCount - 1, i));
    pending.current = idx;
    setActive(idx);
    scroller.current?.scrollTo({left: slides.current[idx]?.offsetLeft ?? 0});
  };

  // Otherwise the visible page is whichever slide sits closest to the scroll
  // position, so swipes and trackpad scrolling keep the indicator in sync.
  useEffect(() => {
    const sc = scroller.current;
    if (!sc) {
      return undefined;
    }
    const onScroll = () => {
      let best = 0;
      let bestDist = Infinity;
      slides.current.forEach((el, i) => {
        const dist = el ? Math.abs(el.offsetLeft - sc.scrollLeft) : Infinity;
        if (dist < bestDist) {
          bestDist = dist;
          best = i;
        }
      });
      if (pending.current !== null) {
        // Ignore the slides passed mid-animation; release once we arrive.
        if (best !== pending.current || bestDist > 1) {
          return;
        }
        pending.current = null;
      }
      setActive(best);
    };
    // A swipe or wheel during the animation hands control back to the scroll position.
    const release = () => {
      pending.current = null;
    };
    sc.addEventListener('scroll', onScroll, {passive: true});
    sc.addEventListener('wheel', release, {passive: true});
    sc.addEventListener('touchstart', release, {passive: true});
    return () => {
      sc.removeEventListener('scroll', onScroll);
      sc.removeEventListener('wheel', release);
      sc.removeEventListener('touchstart', release);
    };
  }, []);

  const step = (direction: 'prev' | 'next') => {
    const from = pending.current ?? active;
    const next = Math.max(0, Math.min(pageCount - 1, from + (direction === 'next' ? 1 : -1)));
    logDashboardInteraction({
      action: 'earnings_page_nav',
      tileId: 'earnings',
      value: direction,
      target: pageLabels[next],
    });
    goTo(next);
  };

  const selectQuarter = (id: string) => {
    if (id === selectedId) {
      return;
    }
    logDashboardInteraction({action: 'earnings_quarter_change', tileId: 'earnings', value: id});
    setSelectedId(id);
    // A new quarter starts from its first metric; jump without the smooth scroll.
    pending.current = null;
    scroller.current?.scrollTo({left: 0, behavior: 'instant'});
    setActive(0);
  };

  if (!d) {
    return null;
  }

  return (
    <Tile
      tileId="earnings"
      label={d.quarterLabel}
      labelAccessory={
        <EarningsQuarterMenu quarters={quarters} selectedId={selectedId} onSelect={selectQuarter} />
      }
      accessory={
        <Split alignY="center" gap="4">
          <Split.Item>
            <IconButton
              variant="borderless"
              size="small"
              disabled={active === 0}
              onClick={() => step('prev')}
              aria-label={t('dashboard_earnings_previous').toString()}
            >
              <UIIcon src={ChevronLeftLine} size="small" />
            </IconButton>
          </Split.Item>
          <Split.Item>
            <IconButton
              variant="borderless"
              size="small"
              disabled={active === pageCount - 1}
              onClick={() => step('next')}
              aria-label={t('dashboard_earnings_next').toString()}
            >
              <UIIcon src={ChevronRightLine} size="small" />
            </IconButton>
          </Split.Item>
        </Split>
      }
    >
      <Stack gap="12" display="flex" height="100%">
        <div
          ref={scroller}
          className={styles.scroller}
          role="region"
          aria-roledescription="carousel"
          aria-label={d.quarterLabel}
        >
          {d.metrics.map((metric, i) => (
            <MetricSlide
              key={metric.label}
              ref={(el) => (slides.current[i] = el)}
              metric={metric}
            />
          ))}
          <OutlookSlide
            ref={(el) => (slides.current[d.metrics.length] = el)}
            guidance={d.guidance}
          />
        </div>
        <Box display="flex" alignItems="center" justifyContent="space-between" style={{gap: 8}}>
          <PageDots count={pageCount} active={active} labels={pageLabels} onSelect={goTo} />
          <Box display="flex" alignItems="center" style={{gap: 12}}>
            <ExternalLink to={d.releaseUrl} target="release" quarter={selectedId}>
              {t('dashboard_earnings_release')}
            </ExternalLink>
            <ExternalLink to={d.callReplayUrl} target="call_replay" quarter={selectedId}>
              {t('dashboard_earnings_call_replay')}
            </ExternalLink>
            <EarningsInfoTooltip title={t('dashboard_earnings_footnote').toString()} />
          </Box>
        </Box>
      </Stack>
    </Tile>
  );
};
